import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Star } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "./AdminLayout";
import { Stars } from "../components/TestimonialForm";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_LABELS = { en_attente: "En attente", approuve: "Approuvé", refuse: "Refusé", publie: "Publié", retire: "Retiré" };
const STATUS_CLS = { en_attente: "bg-amber-100 text-amber-800", approuve: "bg-blue-100 text-blue-800", refuse: "bg-red-100 text-red-800", publie: "bg-green-100 text-green-800", retire: "bg-gray-200 text-gray-700" };
const ACTIONS = {
    en_attente: [["approuve", "Approuver"], ["refuse", "Refuser"]],
    approuve: [["publie", "Publier"], ["refuse", "Refuser"]],
    refuse: [["approuve", "Approuver"]],
    publie: [["retire", "Retirer du site"]],
    retire: [["publie", "Republier"]],
};

export default function Testimonials() {
    const { user: me } = useAuth();
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("");
    const [detail, setDetail] = useState(null);
    const [reason, setReason] = useState("");

    const load = () => api.get("/admin/testimonials", { params: filter ? { status: filter } : {} }).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const toggleFeature = async (t) => {
        try {
            await api.post(`/admin/testimonials/${t.testimonial_id}/feature`, { featured: !t.featured, order: t.featured_order || 99 });
            toast.success(t.featured ? "Retiré de la une." : "Mis en avant (max 5, visible sur /avis et dans l'application).");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const moderate = async (t, action) => {        try {
            await api.post(`/admin/testimonials/${t.testimonial_id}/moderate`, { action, reason });
            toast.success(`Témoignage ${STATUS_LABELS[action].toLowerCase()}.`);
            setReason("");
            setDetail(null);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Supprimer définitivement le témoignage de ${t.name} ?`)) return;
        try {
            await api.delete(`/admin/testimonials/${t.testimonial_id}`);
            toast.success("Témoignage supprimé.");
            setDetail(null);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    return (
        <div data-testid="admin-testimonials">
            <PageHeader title="Modération des témoignages" subtitle="Workflow : Soumis → En attente → Approuvé/Refusé → Publié/Retiré. Aucun témoignage n'est public avant validation." />
            <div className="flex gap-2 mb-6 flex-wrap">
                {[["", "Tous"], ...Object.entries(STATUS_LABELS)].map(([k, l]) => (
                    <button key={k} onClick={() => setFilter(k)} className={`px-4 py-1.5 rounded-full text-sm border ${filter === k ? "bg-forest text-cream border-forest" : "border-border text-pineneedle"}`} data-testid={`filter-${k || "all"}`}>{l}</button>
                ))}
            </div>
            {items && items.length === 0 && <p className="rounded-2xl border-2 border-dashed border-gold/40 bg-white p-10 text-center text-pineneedle" data-testid="testimonials-empty">Aucun témoignage dans cet état.</p>}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {(items || []).map((t) => (
                    <article key={t.testimonial_id} className="rounded-2xl bg-white border border-gold/25 p-5 shadow-sm" data-testid={`testimonial-row-${t.testimonial_id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="font-semibold text-forest">{t.name} {t.rating != null && <Stars value={t.rating} size={13} />}</p>
                                <p className="text-xs text-pineneedle">{t.email} · {fmtDate(t.created_at)}</p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${STATUS_CLS[t.status]}`} data-testid={`testimonial-status-${t.testimonial_id}`}>{STATUS_LABELS[t.status]}</span>
                        </div>
                        <p className="text-sm text-pineneedle mt-3 leading-relaxed line-clamp-3">« {t.text} »</p>
                        <div className="flex flex-wrap gap-1.5 mt-4">
                            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setDetail(t)} data-testid={`detail-${t.testimonial_id}`}>Détails</Button>
                            {t.status === "publie" && (
                                <Button size="sm" variant="outline" className={`h-8 text-xs gap-1 ${t.featured ? "border-gold text-gold-dark" : ""}`} onClick={() => toggleFeature(t)} data-testid={`feature-${t.testimonial_id}`}>
                                    <Star size={13} className={t.featured ? "fill-gold text-gold" : ""} /> {t.featured ? "À la une" : "Mettre en avant"}
                                </Button>
                            )}
                            {(ACTIONS[t.status] || []).map(([action, label]) => (
                                <Button key={action} size="sm" variant={action === "refuse" || action === "retire" ? "outline" : "default"} className={`h-8 text-xs ${action === "refuse" || action === "retire" ? "" : "bg-forest text-cream"}`} onClick={() => moderate(t, action)} data-testid={`${action}-${t.testimonial_id}`}>
                                    {label}
                                </Button>
                            ))}
                            {["admin", "super_admin"].includes(me.role) && (
                                <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-destructive border-destructive/40" onClick={() => remove(t)} data-testid={`delete-testimonial-${t.testimonial_id}`}><Trash2 size={13} /></Button>
                            )}
                        </div>
                    </article>
                ))}
            </div>

            <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
                <DialogContent className="bg-cream max-w-lg" data-testid="testimonial-detail">
                    <DialogHeader><DialogTitle className="font-heading text-forest">Témoignage de {detail?.name}</DialogTitle></DialogHeader>
                    {detail && (
                        <div className="space-y-4">
                            <p className="text-sm text-pineneedle leading-relaxed">« {detail.text} »</p>
                            <div>
                                <Label>Motif interne (facultatif, joint aux prochaines décisions)</Label>
                                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="moderation-reason" />
                            </div>
                            <div>
                                <p className="text-xs font-mono uppercase tracking-widest text-pineneedle mb-2">Historique de modération</p>
                                <div className="space-y-1.5">
                                    {(detail.moderation_history || []).map((h, i) => (
                                        <p key={i} className="text-xs bg-white border rounded-lg px-3 py-2 text-pineneedle">
                                            {STATUS_LABELS[h.action] || h.action} — {h.by} — {fmtDate(h.at)} {h.reason && `· « ${h.reason} »`}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
